import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Spinner, Modal, Form } from 'react-bootstrap';
import {
  fetchAllOrganizationsList,
  fetchOrgRoles,
  fetchAllPermissionsForRoles,
  fetchRolePermissionMappings,
  bulkUpdateRolePermissions,
} from '../../../actioncreators/actioncreators';
import {
  Building2,
  Shield,
  Users,
  Search,
  ChevronDown,
  ChevronRight,
  Save,
  RefreshCw,
  AlertCircle,
  LayoutDashboard,
  BarChart3,
  Clock,
  UserCog,
  Calendar,
  MapPin,
  Navigation,
  FileText,
  Settings,
  Database,
  Eye,
  Edit3,
  Trash2,
  Plus,
  FolderTree,
  Briefcase,
} from 'lucide-react';
import './SuperAdmin.css';

// Icon mapping for different permission modules
const moduleIcons = {
  'General': LayoutDashboard,
  'Dashboard': LayoutDashboard,
  'Analytics': BarChart3,
  'Timesheets': Clock,
  'Users': Users,
  'Members': Users,
  'Work Schedule': Calendar,
  'Locations': MapPin,
  'Tracking': Navigation,
  'Reports': FileText,
  'Audit Logs': FileText,
  'Settings': Settings,
  'Data': Database,
  'Sub Organization': FolderTree,
  'Organization': Briefcase,
};

// Get icon for module
const getModuleIcon = (moduleName) => {
  const Icon = moduleIcons[moduleName] || Settings;
  return Icon;
};

// Get icon for permission type
const getPermissionIcon = (permType) => {
  switch (permType?.toUpperCase()) {
    case 'VIEW':
    case 'MENU':
      return Eye;
    case 'EDIT':
    case 'UPDATE':
      return Edit3;
    case 'DELETE':
      return Trash2;
    case 'CREATE':
    case 'ADD':
      return Plus;
    default:
      return Eye;
  }
};

const RolePermissionManagement = () => {
  const navigate = useNavigate();

  // Guard: only Super Admin can access this page
  const loginResponse = useSelector((state) => state.loginResponse);
  const currentRole = loginResponse?.role_name?.toUpperCase() || loginResponse?.roleName?.toUpperCase() || '';
  const isSuperAdminUser = currentRole === 'SUPER_ADMIN';

  // State for org selection
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showOrgModal, setShowOrgModal] = useState(true);
  const [orgSearchTerm, setOrgSearchTerm] = useState('');

  // State for roles and permissions
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [rolePermissions, setRolePermissions] = useState({});
  const [expandedModules, setExpandedModules] = useState({});

  // State for editing
  const [editedPermissions, setEditedPermissions] = useState(new Set());
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  // Redirect non-Super Admin users away from this page
  useEffect(() => {
    if (!isSuperAdminUser) {
      navigate('/home', { replace: true });
    }
  }, [isSuperAdminUser, navigate]);

  // Loading states
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  // Fetch organizations on mount — only for Super Admin
  useEffect(() => {
    if (isSuperAdminUser) fetchOrganizations();
  }, [isSuperAdminUser]);

  // Fetch roles and permissions when org is selected
  useEffect(() => {
    if (selectedOrg) {
      fetchRolesForOrg();
      fetchPermissionsData();
    }
  }, [selectedOrg]);

  // Fetch role permission mappings when role is selected
  useEffect(() => {
    if (selectedOrg && selectedRole) {
      fetchRoleMappings();
    }
  }, [selectedOrg, selectedRole]);

  const fetchOrganizations = async () => {
    setLoadingOrgs(true);
    try {
      const response = await fetchAllOrganizationsList();
      const orgs = Array.isArray(response.data?.data) ? response.data.data : [];
      setOrganizations(orgs);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    } finally {
      setLoadingOrgs(false);
    }
  };

  const fetchRolesForOrg = async () => {
    setLoadingRoles(true);
    try {
      const response = await fetchOrgRoles(selectedOrg.org_id);
      const rolesData = Array.isArray(response.data?.data) ? response.data.data : [];
      // Filter out SUPER_ADMIN - they have all permissions by default
      const filteredRoles = rolesData.filter(role => role.role_name !== 'SUPER_ADMIN');
      setRoles(filteredRoles);
      if (filteredRoles.length > 0) {
        setSelectedRole(filteredRoles[0]);
      }
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    } finally {
      setLoadingRoles(false);
    }
  };

  const fetchPermissionsData = async () => {
    setLoadingPermissions(true);
    try {
      const response = await fetchAllPermissionsForRoles();
      const permsData = response.data?.data?.permissions || [];
      const grouped = response.data?.data?.grouped || {};
      setPermissions(permsData);
      setGroupedPermissions(grouped);
      // Expand all modules by default
      const expanded = {};
      Object.keys(grouped).forEach(module => {
        expanded[module] = true;
      });
      setExpandedModules(expanded);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const fetchRoleMappings = async () => {
    try {
      const response = await fetchRolePermissionMappings(selectedOrg.org_id, selectedRole.role_name);
      const mappings = response.data?.data?.mappings || [];

      const permMap = {};
      mappings.forEach(m => {
        // Org-specific rows may be granted=false (revocations of system defaults).
        // The backend merges system + org rows so org always wins.
        // Only add to permMap if actually granted.
        if (m.granted !== false) {
          permMap[m.permission_id] = {
            granted: true,
            role_permission_id: m.role_permission_id,
            source: m.source,
            granted_scope: m.granted_scope
          };
        }
      });
      setRolePermissions(permMap);
      setEditedPermissions(new Set(Object.keys(permMap)));
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to fetch role mappings:', error);
    }
  };

  const handleOrgSelect = (org) => {
    setSelectedOrg(org);
    setShowOrgModal(false);
    setSelectedRole(null);
    setRolePermissions({});
    setEditedPermissions(new Set());
  };

  const handleRoleSelect = (role) => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Discard them?')) {
        return;
      }
    }
    setSelectedRole(role);
    setHasChanges(false);
  };

  const handlePermissionToggle = (permissionId) => {
    const newEdited = new Set(editedPermissions);
    if (newEdited.has(permissionId)) {
      newEdited.delete(permissionId);
    } else {
      newEdited.add(permissionId);
    }
    setEditedPermissions(newEdited);
    setHasChanges(true);
  };

  const handleSelectAllModule = (module, perms) => {
    const newEdited = new Set(editedPermissions);
    const allSelected = perms.every(p => newEdited.has(p.permission_id));

    perms.forEach(p => {
      if (allSelected) {
        newEdited.delete(p.permission_id);
      } else {
        newEdited.add(p.permission_id);
      }
    });

    setEditedPermissions(newEdited);
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      await bulkUpdateRolePermissions({
        org_id: selectedOrg.org_id,
        role_name: selectedRole.role_name,
        permissions: Array.from(editedPermissions)
      });
      alert('Permissions saved successfully!');
      setHasChanges(false);
      fetchRoleMappings();
    } catch (error) {
      console.error('Failed to save permissions:', error);
      alert('Failed to save permissions: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    if (window.confirm('Discard all changes?')) {
      fetchRoleMappings();
    }
  };

  const toggleModule = (module) => {
    setExpandedModules(prev => ({
      ...prev,
      [module]: !prev[module]
    }));
  };

  const filteredOrgs = useMemo(() => {
    if (!orgSearchTerm) return organizations;
    return organizations.filter(org =>
      org.org_name?.toLowerCase().includes(orgSearchTerm.toLowerCase()) ||
      org.email?.toLowerCase().includes(orgSearchTerm.toLowerCase())
    );
  }, [organizations, orgSearchTerm]);

  const getPermissionStats = () => {
    const total = permissions.length;
    const selected = editedPermissions.size;
    return { total, selected };
  };

  const stats = getPermissionStats();

  // Block render for non-Super Admin (redirect is handled by useEffect above)
  if (!isSuperAdminUser) return null;

  // Role hierarchy level for display
  const getRoleLevel = (roleName) => {
    const levels = {
      'SUPER_ADMIN': { level: 1, color: '#6366f1' },
      'ADMIN': { level: 2, color: '#8b5cf6' },
      'MANAGER': { level: 3, color: '#0ea5e9' },
      'MEMBER': { level: 4, color: '#10b981' },
    };
    return levels[roleName] || { level: 5, color: '#64748b' };
  };

  // Organization Selection Modal
  if (showOrgModal) {
    return (
      <div className="rpm-container">
        <Modal show={true} onHide={() => navigate(-1)} centered size="lg" className="rpm-org-modal">
          <Modal.Header closeButton className="rpm-modal-header">
            <Modal.Title className="rpm-modal-title">
              <Building2 size={24} />
              Select Organization
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="rpm-modal-body">
            <p className="rpm-modal-subtitle">
              Choose an organization to manage role permissions
            </p>

            <div className="rpm-search-box">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search organizations..."
                value={orgSearchTerm}
                onChange={(e) => setOrgSearchTerm(e.target.value)}
              />
            </div>

            {loadingOrgs ? (
              <div className="rpm-loading">
                <Spinner animation="border" size="sm" />
                <span>Loading organizations...</span>
              </div>
            ) : (
              <div className="rpm-org-list">
                {filteredOrgs.length === 0 ? (
                  <div className="rpm-empty">
                    <AlertCircle size={40} />
                    <p>No organizations found</p>
                  </div>
                ) : (
                  filteredOrgs.map((org) => (
                    <div
                      key={org.org_id}
                      className="rpm-org-item"
                      onClick={() => handleOrgSelect(org)}
                    >
                      <div className="rpm-org-icon">
                        <Building2 size={20} />
                      </div>
                      <div className="rpm-org-info">
                        <span className="rpm-org-name">{org.org_name}</span>
                        <span className="rpm-org-email">{org.email}</span>
                      </div>
                      <span className={`rpm-org-status ${org.status?.toLowerCase()}`}>
                        {org.status}
                      </span>
                      <ChevronRight size={18} />
                    </div>
                  ))
                )}
              </div>
            )}
          </Modal.Body>
        </Modal>
      </div>
    );
  }

  return (
    <div className="rpm-container">
      {/* Top Bar */}
      <div className="rpm-topbar">
        <div className="rpm-topbar-left">
          <button className="rpm-org-selector" onClick={() => setShowOrgModal(true)}>
            <Building2 size={18} />
            <span>{selectedOrg?.org_name}</span>
            <ChevronDown size={16} />
          </button>
        </div>
        <div className="rpm-topbar-right">
          <div className="rpm-permission-count">
            <span className="rpm-count-selected">{stats.selected}</span>
            <span className="rpm-count-separator">/</span>
            <span className="rpm-count-total">{stats.total}</span>
            <span className="rpm-count-label">permissions</span>
          </div>
          {hasChanges && (
            <div className="rpm-actions">
              <button className="rpm-btn-cancel" onClick={handleDiscardChanges}>
                Cancel
              </button>
              <button className="rpm-btn-save" onClick={handleSaveChanges} disabled={saving}>
                {saving ? <Spinner size="sm" animation="border" /> : <Save size={16} />}
                Save
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="rpm-main">
        {/* Roles Panel */}
        <div className="rpm-roles-panel">
          <div className="rpm-panel-title">
            <Shield size={18} />
            <span>Roles</span>
          </div>

          {loadingRoles ? (
            <div className="rpm-loading-small">
              <Spinner size="sm" animation="border" />
            </div>
          ) : (
            <div className="rpm-roles-list">
              {roles.map((role) => {
                const roleInfo = getRoleLevel(role.role_name);
                return (
                  <div
                    key={role.role_name || role.custom_role_id}
                    className={`rpm-role-card ${selectedRole?.role_name === role.role_name ? 'active' : ''}`}
                    onClick={() => handleRoleSelect(role)}
                  >
                    <div
                      className="rpm-role-indicator"
                      style={{ backgroundColor: roleInfo.color }}
                    />
                    <div className="rpm-role-content">
                      <span className="rpm-role-name">{role.role_name}</span>
                      <span
                        className="rpm-role-level"
                        style={{ color: roleInfo.color }}
                      >
                        Level {roleInfo.level}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Permissions Panel */}
        <div className="rpm-permissions-panel">
          {!selectedRole ? (
            <div className="rpm-empty-state">
              <Shield size={48} />
              <p>Select a role to manage permissions</p>
            </div>
          ) : loadingPermissions ? (
            <div className="rpm-loading-state">
              <Spinner animation="border" />
              <p>Loading permissions...</p>
            </div>
          ) : (
            <>
              <div className="rpm-permissions-header">
                <h3>
                  Permissions for <strong>{selectedRole.role_name}</strong>
                </h3>
                <button className="rpm-refresh-btn" onClick={fetchRoleMappings}>
                  <RefreshCw size={16} />
                </button>
              </div>

              <div className="rpm-permissions-grid">
                {Object.entries(groupedPermissions).map(([module, perms]) => {
                  const modulePerms = perms || [];
                  const selectedCount = modulePerms.filter(p => editedPermissions.has(p.permission_id)).length;
                  const allSelected = selectedCount === modulePerms.length && modulePerms.length > 0;
                  const ModuleIcon = getModuleIcon(module);

                  return (
                    <div key={module} className="rpm-permission-group">
                      <div
                        className="rpm-group-header"
                        onClick={() => toggleModule(module)}
                      >
                        <div className="rpm-group-title">
                          {expandedModules[module] ? (
                            <ChevronDown size={16} />
                          ) : (
                            <ChevronRight size={16} />
                          )}
                          <ModuleIcon size={18} />
                          <span>{module}</span>
                          <span className="rpm-group-count">{selectedCount}/{modulePerms.length}</span>
                        </div>
                        <label
                          className="rpm-toggle-switch"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={() => handleSelectAllModule(module, modulePerms)}
                          />
                          <span className="rpm-toggle-slider"></span>
                        </label>
                      </div>

                      {expandedModules[module] && (
                        <div className="rpm-permission-items">
                          {modulePerms.map((perm) => {
                            const isSelected = editedPermissions.has(perm.permission_id);
                            const PermIcon = getPermissionIcon(perm.permission_type);

                            return (
                              <div
                                key={perm.permission_id}
                                className={`rpm-permission-item ${isSelected ? 'active' : ''}`}
                              >
                                <div className="rpm-perm-info">
                                  <PermIcon size={14} />
                                  <span className="rpm-perm-name">{perm.permission_name}</span>
                                </div>
                                <label className="rpm-toggle-switch small">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handlePermissionToggle(perm.permission_id)}
                                  />
                                  <span className="rpm-toggle-slider"></span>
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RolePermissionManagement;
